// Angular
import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, Params, ActivatedRoute } from '@angular/router';

// Angular Material
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

// angulartics
import { Angulartics2 } from 'angulartics2';

// rxjs
import { Subscription } from 'rxjs';

// localforage
import * as localforage from 'localforage';

// Services
import { ApiService } from '@app/services/api/api.service';
import { IdleService } from '@app/services/idle/idle.service';

// Components
import { GuestIdleComponent } from '@app/components/guest-idle/guest-idle.component';

@Component({
  selector: 'oh-survey',
  templateUrl: './survey.component.html',
  styleUrls: ['./survey.component.scss'],
})
export class SurveyComponent implements OnInit, OnDestroy {
  private routeSubscription: Subscription;
  private timeoutSubscription: Subscription;
  private timeoutStartedSubscription: Subscription;
  private token: any;
  private data: any;
  private user: any;
  private openHouse: any;
  private idleDialogRef: MatDialogRef<GuestIdleComponent>;
  private wasSubmitted: boolean;
  public step: number;
  public total: number;
  public questions: any;
  public answers: any;
  public checkedBoxes: any;
  public otherText: any;
  public loading: boolean;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private idleService: IdleService,
    private dialog: MatDialog,
    private apiService: ApiService,
    private snackBar: MatSnackBar,
    private angulartics2: Angulartics2,
  ) {
    this.checkedBoxes = [];
  }

  async ngOnInit() {
    this.idleService.startWatching();

    try {
      this.token = await this.apiService.getTokenFromDatabase();
    } catch (error) {
      console.log(error);
    }

    try {
      this.openHouse = await localforage.getItem('oh-selected-open-house');
    } catch (error) {
      console.log(error);
    }

    try {
      this.answers = await localforage.getItem('oh-active-survey');
      if (!this.answers) {
        this.answers = [];
      }
    } catch (error) {
      console.log(error);
    }

    try {
      this.otherText = await localforage.getItem('oh-survey-other-text');
    } catch (error) {
      console.log(error);
    }

    try {
      this.user = await localforage.getItem('oh-active-user');
    } catch (error) {
      console.log(error);
    }

    await this.getData();
    if (this.total == 0 && !this.wasSubmitted) { // Added by CMinard
      await this.submit();
    }

    this.routeSubscription = this.activatedRoute.queryParams.subscribe((params: Params) => {
      this.handleSurveyRoutes(params);
    });

    this.timeoutStartedSubscription = this.idleService.onTimeoutStarted$.subscribe((value) => {
      if (value === true) {
        this.idleDialogRef = this.dialog.open(GuestIdleComponent, {
          width: '300px',
        });
        this.angulartics2.eventTrack.next({
          action: 'opened',
          properties: { category: 'idleTimer', label: `online: ${navigator.onLine}` },
        });
      }
    });

    this.timeoutSubscription = this.idleService.onTimeout$.subscribe(async (value) => {
      if (value === true) {
        if (!this.wasSubmitted) {
          await this.submitData();
        }
        this.router.navigate(['/welcome']);
        this.angulartics2.eventTrack.next({
          action: 'closedAndReturnedToWelcome',
          properties: {
            category: 'idleTimer',
            label: `open_id: ${this.openHouse.open_id} ~ online: ${navigator.onLine}`,
          },
        });
      } else if (value === false) {
        if (this.idleDialogRef) {
          this.angulartics2.eventTrack.next({
            action: 'closed',
            properties: {
              category: 'idleTimer',
              label: `open_id: ${this.openHouse.open_id} ~ online: ${navigator.onLine}`,
            },
          });
        }
      }
    });
  }

  ngOnDestroy() {
    this.routeSubscription.unsubscribe();
    this.timeoutSubscription.unsubscribe();
    this.timeoutStartedSubscription.unsubscribe();
    this.idleService.destroy();
    if (this.idleDialogRef) {
      this.idleDialogRef.close();
    }
    this.idleDialogRef = null;
  }

  // reset idle timer when user input is detected
  @HostListener('window:keydown')
  @HostListener('window:mousemove')
  @HostListener('window:click')
  @HostListener('window:touchstart')
  @HostListener('window:touchend')
  private resetIdleTimer() {
    this.idleService.resetTimer();
    if (this.idleDialogRef) {
      this.idleDialogRef.close();
    }
  }

  private async getData() {
    try {
      this.data = await this.apiService.getAgentDataFromDatabase();
      this.questions = this.data.survey_questions;
      this.total = this.questions.length;
    } catch (error) {
      console.error(error);
    }
  }

  private handleSurveyRoutes(params: Params) {
    if (params && params.question) {
      this.step = parseInt(params.question, 10);
    }
  }

  private formatSubmissionData() {
    const surveyResponses = [];
    for (const answer of this.answers) {
      const response = {
        qid: answer.question.question_id,
        answer: answer.answer,
      };
      surveyResponses.push(response);
    }
    const submission = {
      open_id: parseInt(this.openHouse.open_id, 10),
      guest: this.user,
      survey_responses: surveyResponses,
    };
    return submission;
  }

  private async savePostData(submission: any) {
    let submissions: any = await localforage.getItem('oh-survey-submissions');
    if (!submissions) {
      submissions = [];
    }
    submissions = [...submissions, submission];
    await localforage.setItem('oh-survey-submissions', submissions);
  }

  private async sendPostDataToApi(submission: any) {
    await this.apiService.postSurvey(this.token, submission);
    console.log('POSTed submission to API: ', submission);
  }

  private async submitData() {
    const submission = this.formatSubmissionData();

    try {
      if (navigator.onLine) {
        await this.sendPostDataToApi(submission);
      } else {
        await this.savePostData(submission);
      }
      await this.apiService.logSignInCount(this.openHouse);
    } catch (error) {
      console.error(error);
      if (typeof error.error === 'string') {
        this.snackBar.open('Error submitting survey: ', error.error, {
          duration: 15000,
        });
      } else {
        this.snackBar.open('Error submitting survey: ', error.error.message, {
          duration: 15000,
        });
      }
    }

    try {
      localforage.removeItem('oh-active-survey');
    } catch (error) {
      console.log(error);
    }
  }

  private async submit() {
    this.loading = true;

    try {
      await localforage.removeItem('oh-survey-other-text');
      await localforage.removeItem('oh-survey-select-answer');
      await this.submitData();
      this.router.navigate([], { queryParams: { drawer: 'survey-thank-you' } });
      this.wasSubmitted = true;
      this.angulartics2.eventTrack.next({
        action: 'submitted',
        properties: { category: 'survey', label: `open_id: ${this.openHouse.open_id} ~ online: ${navigator.onLine}` },
      });
    } catch (error) {
      this.router.navigate(['/welcome']);
      this.angulartics2.eventTrack.next({
        action: 'displayedError',
        properties: { category: 'survey', label: `open_id: ${this.openHouse.open_id} ~ online: ${navigator.onLine}` },
      });
    }

    localforage.removeItem('oh-active-user');
    this.loading = false;
  }

  public checkBox(option: any, event) {
    if (!this.checkedBoxes.includes(option)) {
      this.checkedBoxes.push(option);
    } else {
      const index = this.checkedBoxes.indexOf(option);
      this.checkedBoxes.splice(index, 1);
    }
  }

  public saveCheckboxSelection(question: any) {
    this.answerQuestion(question, this.checkedBoxes);
    this.checkedBoxes = []; // array must be reset so that answers are not saved multiple times -- CMinard
  }

  public async answerQuestion(question: any, answer: string) {

    if (question.response_type === 'select' || question.response_type === 'mortgage' || question.response_type === 'otherAgent') {
      await localforage.setItem('oh-survey-select-answer', answer);
    }

    if (question.permit_other_response === true && answer !== 'other') {
      await localforage.removeItem('oh-survey-other-text');
      this.otherText = null;
    }

    if (question.permit_other_response === true && answer === 'other' && this.otherText) {
      answer = this.otherText;
      await localforage.setItem('oh-survey-other-text', this.otherText);
    }

    // format entry object
    const entry = { step: this.step, question, answer };

    // add entry to rest of answers
    this.answers[entry.step] = entry;
    this.answers = await localforage.setItem('oh-active-survey', this.answers);

    this.continue();
  }

  public continue() {
    if (this.step < this.total - 1) {
      this.step++;
      this.router.navigate([], { queryParams: { question: this.step } });
    } else {
      this.submit();
    }
  }
}
